/**
 * Wizard payload ↔ Strategies API DTO.
 *
 * `transformData()` in `@hooks/useFormSubmit` keeps producing the shape
 * PRD 02 §2 describes — that contract does not move. What this module
 * adds is a **whitelist**: it picks exactly the fields the API accepts
 * and nothing else. The drawer state carries bookkeeping of its own
 * (and a loaded strategy carries server-owned fields), and the DTO
 * refuses unknown keys, so spreading the payload straight into the
 * request would 422 on fields the operator cannot even see.
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v))
const str = (v) => (v === null || v === undefined || v === '' ? null : String(v))
const list = (v) => (Array.isArray(v) ? v.filter(x => x !== null && x !== undefined && x !== '') : [])

/**
 * The axes whose values are Price Seeker ids, read back as numbers.
 *
 * They are stored as text — the engine compares them against
 * `str(id_master_provider)` and `str(id_site)` from the rate shop — so
 * the API hands back `["106"]` where the pickers hold `106`. Nothing
 * matched, the selector opened empty, and the next save wrote the
 * emptiness back: a strategy silently lost its market scope and its
 * competitor guard just by being edited (Curro, 2026-09-28).
 *
 * Only digits are converted. A value that is not a number is some
 * other kind of code and must survive untouched — dropping it would be
 * the same bug wearing a different hat.
 */
export const idList = (v) => list(v).map(x => (/^\d+$/.test(String(x)) ? Number(x) : x))

/**
 * The tariff-type axis of a strategy's scope (CU-869fe6mf4).
 *
 * Two values exist — `'1'` Prepaid, `'0'` Non prepaid, the codes of
 * `PREPAID_OPTIONS` — so the wizard offers a three-state Select and the
 * wire carries a list of at most one. Both values at once is the same
 * rule as none ("every face", UX decision 5), so a scope saved outside
 * the UI with the two of them reads back as unscoped and is saved as
 * such. No exclusion flag travels for this axis: "exclude Prepaid" is
 * "Non prepaid" said another way, and the overlap check must not have
 * to normalise the two spellings.
 */
const TARIFF_TYPES = ['1', '0']
export const tariffTypeList = (v) => {
	const codes = [...new Set(list(v).map(x => (x === true ? '1' : x === false ? '0' : String(x))))]
		.filter(c => TARIFF_TYPES.includes(c))
	return codes.length === 1 ? codes : []
}
/** The Select's key for a stored list: `''` is "All tariff types". */
export const tariffTypeKey = (v) => tariffTypeList(v)[0] ?? ''

/** Fields the server owns or derives; never sent back. */
export const SERVER_OWNED = [
	'id', 'status', 'mode', 'enabled_at', 'created_at', 'updated_at',
	'uid', 'enabled', 'show', 'reportsName',
	'timestampSorted', 'timestamp_create', 'timestamp_update',
]

export function toServer(payload) {
	const strategy = payload.strategy || {}
	const dto = {
		name: payload.name,
		priority: num(payload.priority) ?? 50,
		period: {
			seasons: list(payload.period?.seasons).map(s => ({
				seasonFrom: s.seasonFrom, seasonTo: s.seasonTo,
			})),
			leadTimes: list(payload.period?.leadTimes).map(lt => ({
				leadFrom: num(lt.leadFrom), leadTo: num(lt.leadTo),
			})),
			weekdays: list(payload.period?.weekdays).map(num),
		},
		durations: list(payload.durations).map(d => ({
			durationFrom: num(d.durationFrom), durationTo: num(d.durationTo),
		})),
		compareByCategory: !!payload.compareByCategory,
		// Who the rule is compared against, per axis (CU-869fh646w): off,
		// only offers of the same tariff type / insurance as our own row
		// count; on, every kind on that axis does. Always booleans — the
		// API refuses unknown keys and `undefined` is dropped by JSON.
		compareEveryTariffType: !!payload.compareEveryTariffType,
		compareEveryInsurance: !!payload.compareEveryInsurance,
		filterGroupOffices: list(payload.filterGroupOffices),
		filterGroupSites: list(payload.filterGroupSites),
		filterGroupWebSuppliers: list(payload.filterGroupWebSuppliers),
		filterGroupProviders: list(payload.filterGroupProviders),
		filterProviders: list(payload.filterProviders),
		filterAcriss: list(payload.filterAcriss),
		filterCategories: list(payload.filterCategories),
		// Which of our own faces the rule prices (CU-869fe6mf4). Insurance
		// is an id axis like the providers; tariff type is the list of one
		// above, with no exclusion flag of its own.
		filterInsurances: list(payload.filterInsurances),
		filterTariffTypes: tariffTypeList(payload.filterTariffTypes),
		filterGroupOfficesExclusion: !!payload.filterGroupOfficesExclusion,
		filterGroupSitesExclusion: !!payload.filterGroupSitesExclusion,
		filterGroupWebSuppliersExclusion: !!payload.filterGroupWebSuppliersExclusion,
		filterGroupProvidersExclusion: !!payload.filterGroupProvidersExclusion,
		filterProvidersExclusion: !!payload.filterProvidersExclusion,
		filterAcrissExclusion: !!payload.filterAcrissExclusion,
		filterCategoriesExclusion: !!payload.filterCategoriesExclusion,
		filterInsurancesExclusion: !!payload.filterInsurancesExclusion,
		strategy: strategy.type === 'ranking'
			? {
				type: 'ranking',
				targetPosition: num(strategy.targetPosition),
				keepPosition: !!strategy.keepPosition,
				// Only travels with the guard on: the API rejects a margin the guard would ignore.
				...(strategy.keepPosition
					? { keepPositionMargin: str(strategy.keepPositionMargin ?? null) }
					: {}),
			}
			: {
				type: 'market_trend',
				rankCap: num(strategy.rankCap),
				excludeOutliersPct: str(strategy.excludeOutliersPct ?? null),
			},
		tethers: list(payload.tethers),
		notes: payload.notes ?? null,
	}

	if (payload.undercut && (payload.undercut.value !== null && payload.undercut.value !== undefined)) {
		dto.undercut = {
			value: str(payload.undercut.value),
			type: payload.undercut.type ?? null,
			direction: num(payload.undercut.direction) ?? 0,
		}
	}
	if (payload.competitorRule) {
		dto.competitorRule = {
			neverBelow: list(payload.competitorRule.neverBelow),
			neverAbove: list(payload.competitorRule.neverAbove),
		}
	}
	const conditions = list(payload.advanced?.kpi_conditions)
	if (conditions.length) {
		dto.advanced = {
			kpi_conditions: conditions.map(c => ({ kpi: c.kpi, op: c.op, value: str(c.value) })),
		}
	}
	if (payload.tools?.smartAcrissPricing) {
		dto.tools = { smartAcrissPricing: true }
	}
	return dto
}

/**
 * Server row → what the wizard and the list expect.
 *
 * `enabled` is derived for the list's toggle, which is a boolean, while
 * `status` carries the three real states so the list can tell a draft
 * (never live) from a disabled one (live once, turned off).
 */
export function fromServer(row) {
	const period = row.period || {}
	return {
		...row,
		enabled: row.status === 'enabled',
		// The id axes, as numbers. They are stored as text — the engine
		// compares them against `str(id_master_provider)` and
		// `str(id_site)` from the rate shop — so the API hands back
		// `["106"]` where the pickers hold `106`. Nothing matched, the
		// selector opened empty, and the next save wrote that emptiness
		// back: a strategy lost its market scope and its competitor
		// guard by being opened (Curro, 2026-09-28).
		filterGroupSites: idList(row.filterGroupSites),
		filterGroupWebSuppliers: idList(row.filterGroupWebSuppliers),
		filterGroupProviders: idList(row.filterGroupProviders),
		filterProviders: idList(row.filterProviders),
		filterInsurances: idList(row.filterInsurances),
		// Codes, not ids: the Select keys on `'1'` / `'0'`, and two of two
		// is "all" (see `tariffTypeList`).
		filterTariffTypes: tariffTypeList(row.filterTariffTypes),
		// A strategy saved before the switches existed reads back off
		// (criterion 14): the server sends `false`, and a missing key is
		// the same answer.
		compareEveryTariffType: !!row.compareEveryTariffType,
		compareEveryInsurance: !!row.compareEveryInsurance,
		competitorRule: row.competitorRule
			? {
				...row.competitorRule,
				neverBelow: idList(row.competitorRule.neverBelow),
				neverAbove: idList(row.competitorRule.neverAbove),
			}
			: row.competitorRule,
		period: {
			seasons: period.seasons || [],
			leadTimes: period.leadTimes || [],
			weekdays: period.weekdays || [],
		},
		undercut: row.undercut
			? { ...row.undercut, value: num(row.undercut.value) }
			: null,
		advanced: row.advanced
			? { kpi_conditions: row.advanced.kpi_conditions.map(c => ({ ...c, value: num(c.value) })) }
			: null,
	}
}

/** Strip what the server owns, for a clone. */
export function forClone(row) {
	const out = { ...fromServer(row) }
	for (const key of SERVER_OWNED) delete out[key]
	out.name = null
	return out
}
